import React, { useEffect, useRef, useState } from "react";
import { Box, MenuItem, TextField } from "@mui/material";
import classNames from "classnames";
import toast from "react-hot-toast";
import axiosInstant from "../../../../api";
import {
    createProductPdpUrl,
    generateProductPdpUrlJsonLd,
    getFileIconikJsonLd,
    listProductPdpUrls,
    requestProductPdpUrlScore,
    rescoreProductPdpUrl
} from "../../../../api/endpoints";
import { IconLink, IconPlus } from "../../../../utils/icons";
import { DotLottleLoader } from "../../../../components/Loader";
import { JsonLdValidation } from "../../../../components/JsonLdEligibility/JsonLdEligibility";
import { PageVideo, PdpJsonLdOutput, PdpUrlVideoLink } from "../../../../components/PdpPages/PdpJsonLdOutput";
import { FieldLabel, PDP_FIELD_HELP } from "../../../../components/PdpPages/FieldLabel";
import { PdpChecks } from "../../../../components/PdpPages/PdpChecks";
import { markupSignature, watchMarkupChange } from "../../../../components/PdpPages/markupWatch";
import { useUser } from "../../../../hooks/useUser";
import { useNetworkScope } from "../../../../hooks/useNetworkScope";
import { isInternal } from "../../../../utils";
import { scoringLimitMessage } from "../../../../utils/plans";
import {
    PdpScoreStatus,
    PdpUrlAttempt,
    PdpUrlScore,
    usePendingScorePoll
} from "../../../../components/PdpScoreStatus/PdpScoreStatus";
import { PdpScoreHistory } from "../../../../components/PdpScoreStatus/PdpScoreHistory";
import { PdpPageSummary } from "../../../../components/PdpPages/PdpPageSummary";
import { PdpScoreReportDialog } from "../../../../components/PdpScoreStatus/PdpScoreReportDialog";
import { SkuPdpLinkEditor } from "../../../../components/SkuLinks/SkuPdpLinkEditor";



interface PdpUrl {
    id: number;
    pdp_url: string;
    retailer: string | null;
    // This row's own PDP score cadence, capped by the brand's plan
    // (max_pdp_score_interval). Defaults server-side to weekly or the cap.
    score_interval?: string | null;
    // Scoring active? A row lands paused when the brand's plan already has
    // max_products products scoring; activating it counts against the cap.
    active?: boolean;
    // On autopilot (switched per page in the Autopilot tab).
    autopilot?: boolean;
    // Our videos on the page, one link each (a page carries any number,
    // TDC-1644): the file URL the retailer serves for it, its VideoObject,
    // and the video record for the card. Empty until one is put on it from
    // a video's settings.
    videos?: PdpUrlVideoLink[] | null;
    product_json_ld?: any;
    product_json_ld_validation?: JsonLdValidation | null;
    page_videos?: PageVideo[] | null;
    skus?: string[];
    latest_score?: PdpUrlScore | null;
    latest_attempt?: PdpUrlAttempt | null;
    // Script found → videos found → JSON-LD found, per the last analysis.
    checks?: PdpChecks | null;
}

export interface ProductBrand {
    id: number;
    name?: string;
    rmn?: boolean;
}

interface Props {
    desireid: number;
    // The product's brands. Inside a brand workspace (or as a brand client)
    // a new row belongs to that workspace's brand; internally the operator
    // picks which of these the page belongs to.
    brands?: ProductBrand[] | null;
}

// Brand a new PDP URL row is created against, per context.
export function usePdpUrlBrand(brands?: ProductBrand[] | null) {
    const scope = useNetworkScope();
    const options = React.useMemo(() => (Array.isArray(brands) ? brands.filter((b) => b?.id) : []), [brands]);
    // Workspace (any role) or a brand client anywhere: the scoped brand.
    const workspaceBrandId = scope.inWorkspace || scope.basePath ? scope.brandId : null;
    const [picked, setPicked] = useState<string>("");
    useEffect(() => {
        setPicked(options.length === 1 ? String(options[0].id) : "");
    }, [options]);
    const brandId = workspaceBrandId || (picked ? Number(picked) : null);
    return { brandId, workspaceBrandId, options, picked, setPicked };
}

export const brandOptionLabel = (b: ProductBrand) => `${b.name || `Brand ${b.id}`}${b.rmn ? " · Network" : ""}`;

// Product Pages for a product: a PDP 1 / PDP 2 / Add tab strip, the picked
// page showing its score + history, its copyable JSON-LD + meta tags, and —
// once videos are on it — Generate / Regenerate from their transcripts.
// Same strip and per-page block as the video settings modal
// (PdpUrlsManager); what the video modal adds is putting a video on the
// page and editing that video's details there, since that is where the
// video is in context.
export function ProductPdpUrlsPanel({ desireid, brands }: Props) {
    const { role } = useUser();
    const pdpBrand = usePdpUrlBrand(brands);
    const { brandId } = pdpBrand;
    const [rows, setRows] = useState<PdpUrl[]>([]);
    const [loading, setLoading] = useState(true);
    // Which page the tab strip shows; rows.length = the Add tab. Rows keep
    // their id ASC order, so an index survives the silent refreshes.
    const [pdpIdx, setPdpIdx] = useState(0);
    const [rescoringId, setRescoringId] = useState<number | null>(null);
    const [generatingId, setGeneratingId] = useState<number | null>(null);
    const [generatingProductId, setGeneratingProductId] = useState<number | null>(null);

    const [newUrl, setNewUrl] = useState("");
    // "" = let the API pick the default (weekly, or the brand cap if slower).
    const [adding, setAdding] = useState(false);
    const [newSkus, setNewSkus] = useState<string[]>([]);
    // After an analysis: poll until the row's markup (re-detected page
    // videos, regenerated VideoObjects / Product Object) lands — it is
    // rebuilt in the background after the score returns.
    const markupWatchCancel = useRef<(() => void) | null>(null);
    useEffect(() => () => markupWatchCancel.current?.(), []);
    const [viewingReport, setViewingReport] = useState<{ pdpUrlId: number; scoreId: number; pdpUrl: string } | null>(null);

    // `silent` = background refresh (score polling): swap the rows in place
    // without the full-panel loader.
    const fetchRows = async (did: number, silent = false): Promise<PdpUrl[] | null> => {
        if (!silent) setLoading(true);
        try {
            const { data } = await axiosInstant.get(listProductPdpUrls(did));
            const next: PdpUrl[] = Array.isArray(data) ? data : data?.data || [];
            setRows(next);
            return next;
        } catch (e) {
            if (!silent) {
                toast.error("Couldn't load PDP URLs. Please try again.");
                setRows([]);
            }
            return null;
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (desireid) fetchRows(desireid);
    }, [desireid]);

    // A deleted row can leave the index past the end — fall back to the last
    // page (or the Add tab when none are left).
    useEffect(() => {
        if (!loading && pdpIdx > rows.length) setPdpIdx(rows.length);
    }, [rows.length, loading]);

    // A freshly added URL is scored asynchronously (the API enqueues it and
    // writes a pending attempt) — keep refreshing until it resolves so the
    // scores appear without a reload.
    usePendingScorePoll(rows, () => fetchRows(desireid, true));

    const handleAdd = async () => {
        if (!newUrl.trim()) {
            toast.error("Please enter a PDP URL before saving.");
            return;
        }
        if (!brandId) {
            toast.error(
                pdpBrand.options.length
                    ? "Select the brand this page belongs to."
                    : "No brand on this product yet — set the brand first."
            );
            return;
        }
        setAdding(true);
        try {
            // No video context here — the page starts with no videos on it;
            // they are put on it from a video's settings modal later.
            await axiosInstant.post(createProductPdpUrl(desireid), {
                pdp_url: newUrl.trim(),
                brand_id: brandId,
                skus: newSkus
            });
            toast.success(
                newSkus.length ? "Successfully added PDP URL and SKUs!" : "Successfully added PDP URL!"
            );
            const addedUrl = newUrl.trim();
            setNewUrl("");
            setNewSkus([]);
            const fresh = await fetchRows(desireid);
            // Land on the page just added — that is where its scoring progress shows.
            const idx = (fresh || []).findIndex((r) => r.pdp_url === addedUrl);
            if (idx >= 0) setPdpIdx(idx);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Couldn't add PDP URL. Please try again.");
        } finally {
            setAdding(false);
        }
    };

    const handleRescore = async (row: PdpUrl) => {
        setRescoringId(row.id);
        const prevMarkup = markupSignature(row);
        try {
            const { data } = await axiosInstant.post(rescoreProductPdpUrl(desireid, row.id));
            const body = data?.latest_score ? data : data?.data;
            const fresh: PdpUrlScore | null = body?.latest_score || null;
            if (fresh) {
                // The rescore response carries the same latest_score /
                // latest_attempt shape as the list, so the row is replaced
                // wholesale — no client-side guessing at the attempt.
                const attempt: PdpUrlAttempt | null = body?.latest_attempt || null;
                setRows((prev) =>
                    prev.map((r) => (r.id === row.id ? { ...r, latest_score: fresh, latest_attempt: attempt } : r))
                );
            }
            toast.success("Successfully rescored PDP URL!");
            markupWatchCancel.current?.();
            markupWatchCancel.current = watchMarkupChange({
                rowId: row.id,
                prev: prevMarkup,
                load: () => fetchRows(desireid, true),
                onChange: () => toast.success("JSON-LD updated from the analysis")
            });
        } catch (e: any) {
            toast.error(scoringLimitMessage(e) || e?.response?.data?.message || "Couldn't rescore this PDP URL. Please try again.");
            // Pull the server's view — a failed sync rescore wrote an error
            // attempt we should surface.
            fetchRows(desireid, true);
        } finally {
            setRescoringId(null);
        }
    };

    // Brand roles (admin/member/sales) run reports asynchronously: the API
    // enqueues a forced scoring job and writes a pending attempt, which the
    // existing usePendingScorePoll picks up until the new score (and its
    // stored report) lands.
    const handleRequestScore = async (row: PdpUrl) => {
        setRescoringId(row.id);
        try {
            const { data } = await axiosInstant.post(requestProductPdpUrlScore(desireid, row.id));
            const body = data?.latest_attempt ? data : data?.data;
            const attempt: PdpUrlAttempt | null = body?.latest_attempt || null;
            if (attempt) {
                setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, latest_attempt: attempt } : r)));
            } else {
                fetchRows(desireid, true);
            }
            toast.success("Report started — the score updates here when it finishes.");
        } catch (e: any) {
            const status = e?.response?.status;
            if (status === 409) {
                toast("A report is already running for this page.");
                fetchRows(desireid, true);
            } else if (status === 429) {
                toast.error("You've reached the hourly limit for PDP reports. Try again later.");
            } else {
                toast.error("Couldn't start the report. Please try again.");
            }
        } finally {
            setRescoringId(null);
        }
    };

    // Regenerate the page's VideoObjects from its videos' transcripts — one
    // call per video on the page (the endpoint is keyed by the video, its
    // transcript is the source, and targets one page). Sequential: each
    // spends an LLM call. The list is re-read rather than patched.
    const handleGenerateJsonLd = async (row: PdpUrl) => {
        const links = row.videos || [];
        if (links.length === 0) return;
        setGeneratingId(row.id);
        let failed = 0;
        try {
            await links.reduce<Promise<void>>(
                (chain, link) =>
                    chain.then(async () => {
                        try {
                            await axiosInstant.get(getFileIconikJsonLd(link.s3_file_id, { pdpUrlId: row.id, regenerate: true }));
                        } catch (e) {
                            failed += 1;
                        }
                    }),
                Promise.resolve()
            );
            if (failed === 0) toast.success(links.length === 1 ? "Successfully generated JSON-LD!" : `Generated JSON-LD for ${links.length} videos!`);
            else if (failed < links.length) toast.error(`Generated JSON-LD for ${links.length - failed} of ${links.length} videos.`);
            else toast.error("Couldn't generate JSON-LD. Please try again.");
            await fetchRows(desireid, true);
        } finally {
            setGeneratingId(null);
        }
    };

    // Product Object from the product record — no video needed. The response
    // carries the blob + validation, so the row is patched in place.
    const handleGenerateProductJsonLd = async (row: PdpUrl) => {
        setGeneratingProductId(row.id);
        try {
            const { data } = await axiosInstant.post(generateProductPdpUrlJsonLd(desireid, row.id));
            const body = data?.product_json_ld ? data : data?.data;
            setRows((prev) =>
                prev.map((r) =>
                    r.id === row.id
                        ? {
                              ...r,
                              product_json_ld: body?.product_json_ld ?? r.product_json_ld,
                              product_json_ld_validation: body?.product_json_ld_validation ?? r.product_json_ld_validation
                          }
                        : r
                )
            );
            toast.success("Product Object generated!");
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Couldn't generate the Product Object. Please try again.");
        } finally {
            setGeneratingProductId(null);
        }
    };


    // The sync rescore endpoint is internal-only (proTeamControl); brand
    // users get the async request-score path on the same button.
    const canRescore = isInternal(role);


    if (loading) {
        return <DotLottleLoader fill={false} />;
    }

    const inputWrapCls =
        "flex items-center gap-[9px] px-[14px] py-[11px] border border-border-token rounded-[10px] bg-surface transition-colors focus-within:border-accent";
    const inputCls =
        "field-input flex-1 bg-transparent outline-none text-[13.5px] text-text-1 min-w-0 placeholder:text-muted";

    const isAddTab = pdpIdx >= rows.length;
    const current = rows[pdpIdx];
    const tabCls = (on: boolean) =>
        classNames(
            "inline-flex items-center gap-[6px] px-4 py-[7px] rounded-[7px] text-[13px] font-semibold transition-colors",
            on ? "bg-surface text-text-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]" : "text-muted hover:text-text-2"
        );

    const renderRow = (row: PdpUrl) => {
        const links = row.videos || [];
        return (
            <>
                <PdpPageSummary
                    row={row}
                    desireid={desireid}
                    onChanged={({ deleted }) => fetchRows(desireid, !deleted)}
                />
                <PdpScoreStatus
                    className="mb-3"
                    score={row.latest_score}
                    attempt={row.latest_attempt}
                    onRescore={
                        canRescore ? () => handleRescore(row) : () => handleRequestScore(row)
                    }
                    rescoring={rescoringId === row.id}
                    rescoreLocked={canRescore && rescoringId !== null && rescoringId !== row.id}
                    rescoreLabel={canRescore ? undefined : "Run PDP Analysis"}
                />
                <PdpScoreHistory
                    className="mt-4"
                    productId={desireid}
                    pdpUrlId={row.id}
                    pdpUrl={row.pdp_url}
                    latestScoreId={row.latest_score?.id}
                    onViewReport={(scoreId) =>
                        setViewingReport({ pdpUrlId: row.id, scoreId, pdpUrl: row.pdp_url })
                    }
                />

                <PdpJsonLdOutput
                    className="pt-3 border-t border-border-token"
                    row={row}
                    onGenerate={links.length > 0 ? () => handleGenerateJsonLd(row) : undefined}
                    generating={generatingId === row.id}
                    onGenerateProduct={() => handleGenerateProductJsonLd(row)}
                    generatingProduct={generatingProductId === row.id}
                    noVideoHint="Put a video on this page from the video's settings to generate it."
                />
            </>
        );
    };

    return (
        <Box className="flex flex-col gap-4 w-full pt-4">
            <Box className="border border-border-token rounded-[14px] p-[18px]">
                <Box className="inline-flex flex-wrap gap-[3px] bg-surface-2 rounded-[10px] p-1 mb-[18px]">
                    {rows.map((u, i) => (
                        <button
                            key={u.id}
                            type="button"
                            title={u.pdp_url}
                            onClick={() => setPdpIdx(i)}
                            className={tabCls(pdpIdx === i)}
                        >
                            PDP {i + 1}
                            {u.retailer && (
                                <span className="text-[10px] font-bold text-muted uppercase tracking-[0.4px]">
                                    {u.retailer}
                                </span>
                            )}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={() => setPdpIdx(rows.length)}
                        className={classNames(
                            "inline-flex items-center gap-[5px] px-[14px] py-[7px] rounded-[7px] text-[13px] font-semibold transition-colors",
                            isAddTab
                                ? "bg-surface text-text-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                                : "text-text-2 hover:text-text-1"
                        )}
                    >
                        <IconPlus width={13} height={13} stroke="currentColor" /> Add PDP URL & SKUs
                    </button>
                </Box>

                {!isAddTab && current && renderRow(current)}

                {isAddTab && (
                    <>
                        {rows.length === 0 && (
                            <p className="text-[13px] text-muted leading-[1.5] mb-4">
                                No PDP URLs on this product yet. Add one below with the SKUs it sells. Videos
                                are put on it later from each video&apos;s settings.
                            </p>
                        )}
                        <FieldLabel help={PDP_FIELD_HELP.pdpUrl}>PDP URL</FieldLabel>
                        <Box className={classNames(inputWrapCls, "mb-4")}>
                            <IconLink size={15} stroke="var(--muted)" />
                            <input
                                className={inputCls}
                                placeholder="https://retailer.com/p/product…"
                                value={newUrl}
                                onChange={(e) => setNewUrl(e.target.value)}
                            />
                        </Box>
                        {!pdpBrand.workspaceBrandId && (
                            <>
                                <FieldLabel help={PDP_FIELD_HELP.brand}>Brand</FieldLabel>
                                <Box className="mb-4">
                                    <TextField
                                        select
                                        size="small"
                                        value={pdpBrand.picked}
                                        onChange={(e) => pdpBrand.setPicked(e.target.value)}
                                        helperText="Whose page this is — the retailer for a retail listing, the supplier for its own site."
                                        sx={{ minWidth: 220 }}
                                    >
                                        {pdpBrand.options.map((b) => (
                                            <MenuItem key={b.id} value={String(b.id)}>
                                                {brandOptionLabel(b)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            </>
                        )}
                        <Box className="mb-4">
                            <SkuPdpLinkEditor value={newSkus} onChange={setNewSkus} disabled={adding} />
                        </Box>
                        <button
                            type="button"
                            onClick={handleAdd}
                            disabled={adding}
                            className="px-[22px] py-[10px] rounded-[9px] text-[13.5px] font-semibold text-white bg-accent hover:bg-[#967435] transition-colors disabled:opacity-60"
                        >
                            {adding ? "Adding…" : "Add"}
                        </button>
                    </>
                )}
            </Box>

            {viewingReport && (
                <PdpScoreReportDialog
                    productId={desireid}
                    pdpUrlId={viewingReport.pdpUrlId}
                    scoreId={viewingReport.scoreId}
                    pdpUrl={viewingReport.pdpUrl}
                    onClose={() => setViewingReport(null)}
                />
            )}
        </Box>
    );
}
